// Issue evidence metrics — pure computations over a contract's issue list,
// surfaced in the Performance review drawer as qualitative evidence (NOT a
// grade-modifying input). The four signals mirror the plan:
//
//   1. severityCounts        — open + total count per severity
//   2. medianTimeToResolveDays — only issues that genuinely reached
//                                Resolved (raisedAt → resolvedAt). Issues
//                                closed via superseded / withdrawn /
//                                unresolved_on_record have no resolvedAt
//                                and are excluded — they did not get fixed.
//   3. medianTimeInAwaitingDays — among issues currently in
//                                 `awaiting_response`, how long they've sat
//                                 there (today - statusEnteredAt).
//   4. overdueActionRatio    — overdue actions / total actions across the
//                              register; null when no actions exist.
//   5. standaloneIssues      — open issues with no `links` set, surfaced
//                              separately so a breached contract term with
//                              no link to a scored item still appears in
//                              the review.
//
// All time math uses UTC day boundaries off Brisbane today (the same anchor
// the rest of the surface uses) so the numbers stay deterministic across
// machines.

import type {
  Issue,
  IssueSeverity,
  IssueStatus,
} from "../../../types";

const SEVERITY_ORDER: IssueSeverity[] = ["critical", "high", "medium", "low"];

export interface IssueSeverityCount {
  severity: IssueSeverity;
  /** Issues at this severity not yet in a terminal (closed) state. */
  open: number;
  /** Total issues at this severity, including closed ones. */
  total: number;
}

export interface IssueEvidence {
  severityCounts: IssueSeverityCount[];
  /** Median number of days from raisedAt to resolvedAt across issues that
   *  reached Resolved at some point. `null` when no such issues exist. */
  medianTimeToResolveDays: number | null;
  /** Median days currently sitting in `awaiting_response`. `null` when no
   *  issue is in that state. */
  medianTimeInAwaitingDays: number | null;
  /** Overdue actions / total actions across all issues. `null` when the
   *  register has no actions to report against. */
  overdueActionRatio: number | null;
  /** Total open actions (regardless of overdue) — denominator context. */
  totalActions: number;
  /** Open actions whose dueDate is strictly before `today` and which are
   *  not yet completed. */
  overdueActions: number;
  /** Open issues (not closed) with no `links` entries — i.e. no link to a
   *  scored register item. These don't pin to a deliverable / milestone /
   *  KPI in the review surface so they need their own callout. */
  standaloneOpenIssues: Issue[];
}

const DAY_MS = 86_400_000;

function startOfUTCDayFromIso(iso: string): number | null {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  const t = d.getTime();
  return Number.isNaN(t) ? null : t;
}

function diffDays(later: number, earlier: number): number {
  return Math.round((later - earlier) / DAY_MS);
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid]!;
  return (sorted[mid - 1]! + sorted[mid]!) / 2;
}

function isNonTerminal(status: IssueStatus): boolean {
  return status !== "closed";
}

export function computeIssueEvidence(
  issues: Issue[],
  todayIso: string,
): IssueEvidence {
  const todayTs = startOfUTCDayFromIso(todayIso);

  const severityCounts: IssueSeverityCount[] = SEVERITY_ORDER.map((sev) => {
    const subset = issues.filter((i) => i.severity === sev);
    return {
      severity: sev,
      open: subset.filter((i) => isNonTerminal(i.status)).length,
      total: subset.length,
    };
  });

  // Median time-to-resolve: raisedAt → resolvedAt. resolvedAt is stamped
  // only on transitions to Resolved (or close-with-outcome 'resolved'),
  // so withdrawn / superseded / unresolved-on-record have no resolvedAt
  // and drop out of the population naturally.
  const resolveDurations: number[] = [];
  for (const i of issues) {
    if (!i.resolvedAt) continue;
    const raised = startOfUTCDayFromIso(i.raisedAt);
    const resolved = startOfUTCDayFromIso(i.resolvedAt);
    if (raised === null || resolved === null) continue;
    resolveDurations.push(Math.max(0, diffDays(resolved, raised)));
  }
  const medianTimeToResolveDays = median(resolveDurations);

  // Median time in awaiting_response — only issues currently sitting in
  // that state, measured from statusEnteredAt to today.
  const awaitingDurations: number[] = [];
  if (todayTs !== null) {
    for (const i of issues) {
      if (i.status !== "awaiting_response") continue;
      const entered = startOfUTCDayFromIso(i.statusEnteredAt);
      if (entered === null) continue;
      awaitingDurations.push(Math.max(0, diffDays(todayTs, entered)));
    }
  }
  const medianTimeInAwaitingDays = median(awaitingDurations);

  // Overdue actions: ratio of open actions whose dueDate is strictly before
  // today. Done actions never count as overdue regardless of dueDate. An
  // empty register returns null so the panel can render "—" rather than
  // a misleading 0.
  let totalActions = 0;
  let overdueActions = 0;
  if (todayTs !== null) {
    for (const i of issues) {
      const actions = Object.values(i.actions ?? {});
      for (const a of actions) {
        totalActions += 1;
        if (a.doneAt) continue;
        const due = startOfUTCDayFromIso(a.dueDate);
        if (due !== null && due < todayTs) overdueActions += 1;
      }
    }
  }
  const overdueActionRatio =
    totalActions === 0 ? null : overdueActions / totalActions;

  // Standalone OPEN issues: links empty + non-closed. A closed standalone
  // issue still has audit value but the review focus is what's live.
  const standaloneOpenIssues = issues.filter(
    (i) =>
      isNonTerminal(i.status) && (!i.links || i.links.length === 0),
  );

  return {
    severityCounts,
    medianTimeToResolveDays,
    medianTimeInAwaitingDays,
    overdueActionRatio,
    totalActions,
    overdueActions,
    standaloneOpenIssues,
  };
}
